import React from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const navLinks = [
  { label: "How it works", href: "#how" },
  { label: "Services", href: "#services" },
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  return (
    <motion.header
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="main-header"
      className="sticky top-0 z-50 w-full border-b border-black/5 backdrop-blur-xl bg-white/70"
    >
      <div className="px-6 md:px-12 lg:px-24 flex h-16 md:h-20 items-center justify-between">
        {/* Logo */}
        <a
          href="#top"
          data-testid="header-logo"
          className="flex items-center"
          aria-label="Raanah home"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_task-escrow-platform/artifacts/xsz4zyvy_Raanah%20Logo-01%20%282%29.jpg"
            alt="Raanah"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-[#475569] hover:text-[#0A0A0A] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          {isAuthed ? (
            <>
              <Link
                to={`/dashboard/${user.role}`}
                data-testid="header-dashboard-link"
                className="text-sm font-medium text-[#0A0A0A] hover:text-[#1E4D55] transition-colors"
              >
                Go to dashboard
              </Link>
              <Link
                to={`/dashboard/${user.role}`}
                data-testid="header-cta-button"
                className="bg-[#0A0A0A] text-white hover:bg-[#1E4D55] transition-colors px-5 py-2.5 text-sm font-semibold"
              >
                {user.name?.split(" ")[0] || "Account"}
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-login-link"
                className="text-sm font-medium text-[#0A0A0A] hover:text-[#1E4D55] transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="header-cta-button"
                className="bg-[#0A0A0A] text-white hover:bg-[#1E4D55] transition-colors px-5 py-2.5 text-sm font-semibold"
              >
                Get early access
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          aria-label="Toggle menu"
          data-testid="header-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-[#0A0A0A]"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-[#0A0A0A]"
              >
                {l.label}
              </a>
            ))}
            {isAuthed ? (
              <Link
                to={`/dashboard/${user.role}`}
                onClick={() => setOpen(false)}
                className="bg-[#0A0A0A] text-white text-center py-3 text-sm font-semibold"
              >
                Go to dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium text-[#0A0A0A]"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  className="bg-[#0A0A0A] text-white text-center py-3 text-sm font-semibold"
                >
                  Get early access
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </motion.header>
  );
}
